import { Fragment } from "react";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { STEPS, SECTIONS } from "@/components/job/Lifecycle";
import { journeyNext } from "@/components/copilot/useJourney";
import { AskTeammateDialog } from "@/components/assessment/AskTeammates";

export function JourneySteps({ job }) {
  const lc = job.lifecycle || { step: 1 };
  const live = job.status === "published";
  return (
    <ol className="flex flex-wrap items-center gap-y-2 min-w-0" aria-label="Hiring journey" data-testid="journey-steps">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < lc.step || (live && n <= 3);
        const current = !done && n === lc.step;
        return (
          <Fragment key={label}>
            <li className="flex items-center gap-1.5" title={label} data-testid={`journey-step-${SECTIONS[i]}`} data-state={done ? "done" : current ? "current" : "todo"} aria-current={current ? "step" : undefined}>
              <span className={cn("h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0", done ? "bg-primary/15 text-primary" : current ? "bg-primary text-primary-foreground" : "border border-foreground/20 text-muted-foreground")}>{done ? <Check className="h-3 w-3" /> : n}</span>
              <span className={cn("text-xs whitespace-nowrap", current ? "text-foreground font-semibold" : "journey-todo-label text-muted-foreground")}>{label}</span>
            </li>
            {i < STEPS.length - 1 && <li aria-hidden className={cn("mx-1.5 h-px w-3", done ? "bg-primary/50" : "bg-border")} />}
          </Fragment>
        );
      })}
    </ol>
  );
}

function Primary({ action, actions, testId }) {
  return (
    <button onClick={action.run} disabled={!!actions.working || actions.busy} data-testid={testId}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground font-semibold disabled:opacity-50 hover:brightness-110 transition-[filter] h-9 px-4 text-sm">
      {actions.working === action.key && <Loader2 className="h-3.5 w-3.5 motion-safe:animate-spin" />}
      {action.label}<ArrowRight className="h-3.5 w-3.5" />
    </button>
  );
}

const sectionOf = (url) => url?.match(/^\/recruiter\/jobs\/[^/?#]+\/([a-z]+)/)?.[1];

// When the open preview shows the page that owns this step, the buttons live there and the card only explains what's next.
export function NextStepCard({ job, actions, previewOpen, previewUrl }) {
  const next = journeyNext(job, actions);
  const owned = previewOpen && !!next.section && sectionOf(previewUrl) === next.section;
  return (
    <div className="sm:ml-8 rounded-2xl border border-primary/30 bg-primary/[0.04] p-4 space-y-3 motion-safe:animate-in motion-safe:fade-in" data-testid="ai-next-step" data-owned={owned}>
      {!previewOpen && <JourneySteps job={job} />}
      <div className="space-y-1">
        <p className="hs-mono text-[11px] uppercase tracking-wider text-primary">Next step</p>
        <p className="font-display text-base font-semibold text-foreground" data-testid="ai-next-step-title">{next.title}</p>
        <p className="text-sm text-muted-foreground leading-6" data-testid="ai-next-step-why">{next.why}</p>
      </div>
      {owned ? <p className="text-xs font-medium text-primary" data-testid="ai-next-step-hint">Continue in the preview on the right.</p> : (
        <div className="flex flex-wrap gap-2">
          {next.primary && <Primary action={next.primary} actions={actions} testId="ai-next-primary" />}
          {next.actions.map((x, i) => <button key={x.label} onClick={x.run} disabled={!!actions.working || actions.busy} data-testid={`ai-next-secondary-${i}`} className="h-9 px-3 rounded-lg border border-border text-sm hover:border-primary/50 hover:text-primary disabled:opacity-50 transition-colors">{x.label}</button>)}
        </div>
      )}
      <AskTeammateDialog trialId={job.lifecycle?.latest_trial_id} open={!!actions.asking} onOpenChange={actions.setAsking} onSent={actions.refresh} />
    </div>
  );
}

export function PreviewJourney({ job }) {
  return (
    <div className="flex flex-1 min-w-0 items-center overflow-x-auto no-scrollbar" data-testid="ai-preview-journey">
      <JourneySteps job={job} />
    </div>
  );
}
